import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import ProductCard from "@/components/ProductCard";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";
import { MapPin, MessageSquare, Store, Megaphone, MailPlus, X, Send, ImagePlus, ShoppingCart } from "lucide-react";
import { bannerBackground, accentColor } from "@/lib/theme";
import { toast } from "sonner";
import { useCart } from "@/hooks/useCart";
import { CartDrawer } from "@/components/CartDrawer";
import BetaVendorBadge from "@/components/BetaVendorBadge";

export default function Shop() {
  const { username } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRequest, setShowRequest] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const cart = useCart(username);

  useEffect(() => {
    api.get(`/shop/${username}`)
      .then((r) => setShop(r.data))
      .catch(() => setShop(false))
      .finally(() => setLoading(false));
  }, [username]);

  if (loading) return (<div className="p-8 text-[#857163]">Loading shop…</div>);
  if (shop === false) return (<div className="p-8 text-[#857163]">Shop not found</div>);

  const s = shop.seller;
  const isMe = user && user !== false && user.id === s.user_id;
  const banner = s.banner_url ? fileUrl(s.banner_url) : null;
  const accent = accentColor(s);

  return (
    <>
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 lg:py-10" style={{ ["--shop-accent"]: accent }}>
        <div
          className="rounded-3xl h-48 sm:h-56 mb-[-40px] border border-[#EBDCD1] bg-cover bg-center"
          style={{ background: bannerBackground(s, banner) }}
          data-testid="shop-banner"
        />

        <div className="cp-card p-6 sm:p-8 relative">
          <div className="flex items-start gap-4 flex-wrap">
            <Avatar className="w-20 h-20 border-4 border-white shadow-md -mt-14">
              <AvatarImage src={fileUrl(s.avatar_url)} />
              <AvatarFallback className="text-[#3B2C24] font-heading text-2xl font-bold" style={{ background: accent, color: "#fff" }}>
                {s.business_name?.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-[#3B2C24]">{s.business_name}</h1>
                {s.is_beta_tester && <BetaVendorBadge program={s.beta_program} />}
              </div>
              <div className="text-sm text-[#857163] flex items-center gap-2 flex-wrap">
                <Link to={`/u/${s.username}`} className="hover:underline">@{s.username}</Link>
                {s.city && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {s.city}</span>}
              </div>
              {s.tagline && <p className="mt-1 text-[#3B2C24]">{s.tagline}</p>}
              {s.about && <p className="mt-2 text-sm text-[#857163] leading-relaxed max-w-2xl whitespace-pre-wrap">{s.about}</p>}
              <div className="mt-4 flex gap-2 flex-wrap">
                {isMe ? (
                  <Link to="/seller/dashboard" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white font-semibold text-sm hover:opacity-90 transition" style={{ background: accent }} data-testid="manage-shop-btn">
                    <Store className="w-4 h-4" /> Manage shop
                  </Link>
                ) : user && user !== false && (
                  <button onClick={() => nav(`/messages/${s.user_id}`)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white font-semibold text-sm hover:opacity-90 transition" style={{ background: accent }} data-testid="dm-shop-btn">
                    <MessageSquare className="w-4 h-4" /> Message
                  </button>
                )}
                {!isMe && (
                  <button onClick={() => setShowRequest(true)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border-2 font-semibold text-sm hover:bg-[#FFF9EF] transition" style={{ borderColor: accent, color: accent }} data-testid="open-custom-request-btn">
                    <MailPlus className="w-4 h-4" /> Custom request
                  </button>
                )}
                {!isMe && (
                  <button onClick={() => setShowCart(true)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white font-semibold text-sm hover:opacity-90 transition relative" style={{ background: accent }} data-testid="open-cart-btn">
                    <ShoppingCart className="w-4 h-4" /> Cart
                    {cart.itemCount > 0 && (
                      <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-white text-[10px] font-bold" style={{ color: accent }} data-testid="cart-badge">{cart.itemCount}</span>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Shop announcement banner — vendor-editable, appears above the products grid */}
        {s.shop_announcement && (
          <div
            className="mt-5 rounded-2xl p-4 flex items-start gap-3 border-2"
            style={{ background: "#FFF9EF", borderColor: `${accent}55` }}
            data-testid="shop-announcement"
          >
            <div className="w-9 h-9 grid place-items-center rounded-full shrink-0" style={{ background: accent, color: "#fff" }}>
              <Megaphone className="w-4 h-4" />
            </div>
            <div className="text-sm text-[#3B2C24] leading-relaxed whitespace-pre-wrap">{s.shop_announcement}</div>
          </div>
        )}

        <h2 className="font-heading text-2xl font-semibold text-[#3B2C24] mt-8 mb-4 flex items-center gap-3">
          <span>Products</span>
          <span className="h-1 flex-1 rounded-full opacity-60" style={{ background: accent }} />
        </h2>
        {shop.products.length === 0 ? (
          <div className="cp-card p-8 text-center text-[#857163]">No products listed yet.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="shop-products">
            {shop.products.map((p) => (
              <ProductCard
                key={p.id}
                product={{ ...p, seller: s }}
                showAddToCart={!isMe}
                onAddToCart={(qty = 1) => {
                  cart.addItem({
                    product_id: p.id,
                    title: p.title,
                    image_url: (p.images || [])[0] || null,
                    unit_price_cents: (p.discount_pct ? Math.round(p.price_cents * (100 - p.discount_pct) / 100)
                      : p.discount_cents ? Math.max(0, p.price_cents - p.discount_cents)
                      : p.price_cents),
                    quantity: qty,
                  });
                  toast.success(`${p.title} added to cart`);
                }}
              />
            ))}
          </div>
        )}
      </main>
      <CartDrawer
        username={username}
        businessName={s.business_name}
        accent={accent}
        open={showCart}
        onClose={() => setShowCart(false)}
      />
      {showRequest && (
        <CustomRequestModal
          shopUsername={s.username}
          businessName={s.business_name}
          accent={accent}
          onClose={() => setShowRequest(false)}
        />
      )}
    </>
  );
}

function CustomRequestModal({ shopUsername, businessName, accent, onClose }) {
  const { user } = useAuth();
  const [customerName, setCustomerName] = useState(user?.username || "");
  const [customerEmail, setCustomerEmail] = useState(user?.email || "");
  const [description, setDescription] = useState("");
  const [budget, setBudget] = useState("");
  const [neededBy, setNeededBy] = useState("");
  const [refImage, setRefImage] = useState(null); // uploaded URL path
  const [uploadingRef, setUploadingRef] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const uploadReference = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploadingRef(true);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRefImage(data.path);
      toast.success("Reference photo attached");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploadingRef(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (description.trim().length < 10) { toast.error("Please describe what you'd like in at least a sentence."); return; }
    setSubmitting(true);
    try {
      await api.post(`/shop/${shopUsername}/custom-request`, {
        customer_name: customerName.trim(),
        customer_email: customerEmail.trim(),
        description: description.trim(),
        budget: budget.trim() || null,
        needed_by: neededBy.trim() || null,
        reference_image_url: refImage || null,
      });
      setSent(true);
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Couldn't submit your request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="custom-request-modal">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2 font-heading text-lg text-[#3B2C24]">
            <MailPlus className="w-4 h-4" style={{ color: accent }} /> Custom request
          </div>
          <button onClick={onClose} className="text-[#857163] hover:text-[#3B2C24]"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-[#857163] mb-4">Tell {businessName} what you have in mind — flavors, quantity, event date. They'll reply directly by email.</p>
        {sent ? (
          <div className="text-center py-4" data-testid="custom-request-sent">
            <div className="w-14 h-14 grid place-items-center rounded-full mx-auto mb-2" style={{ background: `${accent}22`, color: accent }}>
              <Send className="w-6 h-6" />
            </div>
            <div className="font-heading text-lg text-[#3B2C24]">Request sent!</div>
            <div className="text-xs text-[#857163] mt-1">Check your inbox for a confirmation. {businessName} will reply shortly.</div>
            <button onClick={onClose} className="cp-btn-primary mt-4 w-full" style={{ background: accent }}>Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-2.5">
            <label className="block">
              <span className="text-xs text-[#857163]">Your name</span>
              <input className="cp-input mt-1" value={customerName} onChange={(e) => setCustomerName(e.target.value)} required maxLength={80} data-testid="cr-name" />
            </label>
            <label className="block">
              <span className="text-xs text-[#857163]">Your email</span>
              <input type="email" className="cp-input mt-1" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} required data-testid="cr-email" />
            </label>
            <label className="block">
              <span className="text-xs text-[#857163]">What are you looking for?</span>
              <textarea className="cp-input mt-1 min-h-[90px] resize-y" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={4000} placeholder="24 gold-glitter cake pops for a baby shower on Saturday…" required data-testid="cr-description" />
              <div className="text-[10px] text-[#857163] text-right mt-0.5">{description.length}/4000</div>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="text-xs text-[#857163]">Budget <span className="text-[#B79885]">(optional)</span></span>
                <input className="cp-input mt-1" value={budget} onChange={(e) => setBudget(e.target.value)} maxLength={80} placeholder="$80" data-testid="cr-budget" />
              </label>
              <label className="block">
                <span className="text-xs text-[#857163]">Needed by <span className="text-[#B79885]">(optional)</span></span>
                <input className="cp-input mt-1" value={neededBy} onChange={(e) => setNeededBy(e.target.value)} maxLength={80} placeholder="Dec 15" data-testid="cr-needed-by" />
              </label>
            </div>
            <div>
              <span className="text-xs text-[#857163]">Reference photo <span className="text-[#B79885]">(optional)</span></span>
              {refImage ? (
                <div className="mt-1 relative inline-block" data-testid="cr-reference-preview">
                  <img src={fileUrl(refImage)} alt="reference" className="w-24 h-24 rounded-xl object-cover border border-[#EBDCD1]" />
                  <button type="button" onClick={() => setRefImage(null)} className="absolute -top-1 -right-1 bg-white rounded-full p-0.5 border border-[#EBDCD1] hover:bg-[#F9EFE6]" data-testid="cr-reference-remove"><X className="w-3 h-3" /></button>
                </div>
              ) : (
                <label className="mt-1 flex items-center gap-2 cursor-pointer text-sm text-[#3B2C24] bg-white border border-dashed border-[#EBDCD1] rounded-xl px-3 py-2 hover:bg-[#F9EFE6]" data-testid="cr-reference-upload">
                  <input type="file" accept="image/*" className="hidden" onChange={uploadReference} disabled={uploadingRef} />
                  <ImagePlus className="w-4 h-4" style={{ color: accent }} />
                  <span className="text-[#857163]">{uploadingRef ? "Uploading…" : "Attach an inspiration photo"}</span>
                </label>
              )}
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full mt-2 py-2.5 rounded-full text-white font-semibold disabled:opacity-60"
              style={{ background: accent }}
              data-testid="cr-submit"
            >
              {submitting ? "Sending…" : "Send request"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
